import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { useGetEmailDetailsQuery } from "@/services/conciliation/email-report";
import { formatToBRL } from "@/utils/formatToMoney";
import { formatOperatorName } from "@/utils/format-operator-name";
import { getEmailTypeLabel } from "./conciliation-email-report-types";
import { EmailStatusBadge } from "./email-status-badge";
import { Loader2, ArrowLeft } from "lucide-react";

interface EmailDetailsModalProps {
  emailId: number;
}

// Usar o mesmo padrão de status do email-review
const getStatusVariant = (status: string): "default" | "destructive" | "secondary" => {
  const map: Record<string, "default" | "destructive" | "secondary"> = {
    APPROVED: "default",
    RESPONDED: "default",
    REJECTED: "destructive",
    REJECTED_WAITING_FOR_RESPOND: "destructive",
    PENDING_ANSWER: "secondary",
  };
  return map[status] ?? "secondary";
};

const getStatusLabel = (status: string): string => {
  const map: Record<string, string> = {
    APPROVED: "Aprovado",
    RESPONDED: "Respondido",
    REJECTED: "Reprovado",
    REJECTED_WAITING_FOR_RESPOND: "Aguardando Nova Resposta",
    PENDING_ANSWER: "Aguardando Resposta",
  };
  return map[status] ?? "Aguardando Resposta";
};

export function EmailDetailsModal({ emailId }: EmailDetailsModalProps) {
  const { handleModal } = useDialogModal();
  const { data, isLoading, error } = useGetEmailDetailsQuery(emailId);

  const handleClose = () => handleModal({ isOpen: false });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Carregando detalhes...</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-4 text-center">
        <p className="text-gray-600">Erro ao carregar detalhes do e-mail.</p>
        <Button variant="outline" className="mt-4" onClick={handleClose}>
          Fechar
        </Button>
      </div>
    );
  }

  const allItems = [
    ...data.divergences.map((d) => ({ ...d, itemType: "DIVERGENCE" as const })),
    ...data.nsus.map((n) => ({ ...n, itemType: "NSU" as const })),
  ];

  return (
    <div className="flex flex-col h-[calc(70vh-2rem)] min-w-[500px] max-w-[700px]">
      {/* Header com informações do email */}
      <div className="pb-3 border-b flex-shrink-0">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <span className="text-gray-500 text-xs">Loja</span>
            <p className="font-medium">{data.store_code}</p>
          </div>
          <div>
            <span className="text-gray-500 text-xs">Regional</span>
            <p className="font-medium">{data.regional_name || "-"}</p>
          </div>
          <div>
            <span className="text-gray-500 text-xs">Tipo</span>
            <p className="font-medium">{getEmailTypeLabel(data.type)}</p>
          </div>
          <div>
            <span className="text-gray-500 text-xs">Enviado em</span>
            <p className="font-medium">
              {format(new Date(data.sent_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between mt-3 pt-3 border-t">
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-xs">Status:</span>
            <EmailStatusBadge
              status={data.aggregated_status}
              itemsSummary={data.items_summary}
            />
          </div>
          <div className="text-xs text-gray-600">
            <span className="font-medium">Analista: </span>
            {formatOperatorName(data.requested_by_email)}
          </div>
        </div>
      </div>

      {/* Lista de itens */}
      <ScrollArea className="flex-1 overflow-hidden mt-3">
        <div className="space-y-2 pr-2">
          {allItems.length === 0 ? (
            <p className="text-center text-gray-500 py-8">Nenhum item neste e-mail</p>
          ) : (
            allItems.map((item) => (
              <Card key={`${item.itemType}-${item.review_id}`}>
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      {/* Header do item */}
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">
                            {item.itemType === "DIVERGENCE" ? item.name : item.number}
                          </div>
                          <div className="text-xs text-gray-600">
                            {item.itemType === "DIVERGENCE" ? "Divergência" : "NSU"} • {item.operator}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Badge variant={getStatusVariant(item.status)} className="text-xs">
                            {getStatusLabel(item.status)}
                          </Badge>
                          <span className="font-semibold text-sm whitespace-nowrap">
                            {formatToBRL(item.amount)}
                          </span>
                        </div>
                      </div>

                      {/* Justificativa da loja (apenas divergências) */}
                      {item.itemType === "DIVERGENCE" && item.store_justification && (
                        <div className="text-xs text-gray-500 mt-1">
                          <span className="font-medium">Justificativa:</span> {item.store_justification}
                        </div>
                      )}

                      {/* Arquivos anexados */}
                      {item.files && item.files.length > 0 && (
                        <div className="text-xs text-gray-500 mt-1">
                          {item.files.length} arquivo(s) anexado(s)
                        </div>
                      )}

                      {/* Info de revisão */}
                      {item.reviewed_at && (
                        <div className="text-xs text-gray-400 mt-1 italic">
                          ✓ Revisado em {format(new Date(item.reviewed_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                          {item.reviewed_by && ` por ${formatOperatorName(item.reviewed_by)}`}
                        </div>
                      )}

                      {/* Observação da revisão */}
                      {item.review_notes && (
                        <div className="mt-2 p-2 bg-gray-100 rounded text-xs text-gray-600">
                          <span className="font-medium">Observação:</span><br />
                          {item.review_notes}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </ScrollArea>

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t bg-white flex-shrink-0 mt-3">
        <Button variant="outline" onClick={handleClose}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          Voltar
        </Button>
        <div className="text-xs text-gray-500">
          {allItems.length} item(s) no total
        </div>
      </div>
    </div>
  );
}
